import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import { CardSkeleton } from '@/components/Loading';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { Building2, MapPin, Search } from 'lucide-react';

export default function LocaliList() {
  const { t } = useTranslation();
  const [locali, setLocali] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [zona, setZona] = useState('all');

  useEffect(() => {
    Promise.all([api.get('/public/locali'), api.get('/public/zones')])
      .then(([l, z]) => { setLocali(l.data || []); setZones(z.data?.zones || []); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => locali.filter(l => {
    if (zona !== 'all' && l.zona !== zona) return false;
    if (q && !l.nome.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [locali, zona, q]);

  return (
    <PublicLayout>
      <SEO title="Locali a Roma | Roma Nightlife" description="Scopri i migliori locali, club e bar di Roma. Tutte le zone: Testaccio, EUR, Centro, Ponte Milvio." />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 hero-overlay relative">
        <p className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold)] mb-2 relative z-10">Roma • Locali</p>
        <h1 className="font-heading text-4xl md:text-5xl relative z-10">{t('locale.title')}</h1>
        <p className="mt-3 text-[color:var(--text-muted)] max-w-2xl relative z-10">Tutti i locali e i club partner della scena nightlife romana, divisi per zona.</p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-3 grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-subtle)]" />
            <Input data-testid="locali-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca locale…" className="pl-9 bg-[color:var(--surface-2)] border-[color:var(--border-c)]" />
          </div>
          <Select value={zona} onValueChange={setZona}>
            <SelectTrigger data-testid="locali-filter-zone" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]">
              <SelectValue placeholder="Zona" />
            </SelectTrigger>
            <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
              <SelectItem value="all">{t('filters.anyZone')}</SelectItem>
              {zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? [...Array(6)].map((_, i) => <CardSkeleton key={i} />) : filtered.length === 0 ? (
            <div className="col-span-full text-center py-16 rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)]">
              <Building2 className="w-10 h-10 mx-auto text-[color:var(--gold)] opacity-40 mb-3"/>
              <p className="text-[color:var(--text-muted)]">{t('empty.venues')}</p>
            </div>
          ) : filtered.map((l) => (
            <Link key={l.id} to={`/locali/${l.slug}`} data-testid="locale-card" className="group rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)] overflow-hidden hover:border-[color:var(--gold)]">
              <div className="aspect-[16/9] bg-[color:var(--surface-2)]">
                {(l.gallery?.[0] || l.logo) ? <img src={l.gallery?.[0] || l.logo} alt={l.nome} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"/> : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-10 h-10 text-[color:var(--gold)] opacity-40"/></div>}
              </div>
              <div className="p-4">
                <h3 className="font-heading text-xl group-hover:text-[color:var(--gold)]">{l.nome}</h3>
                <p className="text-xs text-[color:var(--text-muted)] flex items-center gap-1 mt-1"><MapPin className="w-3 h-3"/> {l.zona}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
